import { useDebounce } from '@neo/hooks/use-debounce'
import { Button } from '@neo/primitives/button'
import { DialogFooter } from '@neo/primitives/dialog'
import { Input } from '@neo/primitives/input'
import { Skeleton } from '@neo/primitives/skeleton'
import { Typography } from '@neo/primitives/typography'
import { usePlayAudio } from '@shared/hooks/use-play-audio'
import { Loader2, SearchIcon } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { QueryError } from 'v2/api-console/shared/components/query-error'
import type { CommunityVoice } from 'v2/pages/flow/types'
import { errorToast } from 'v2/utils/toasts'
import { VoiceItem } from '../components/voice-item'
import type { CommunityVoicesFormValues } from '../custom-voice.types'
import { MIN_SEARCH_LENGTH, useCommunityVoicesInfiniteQuery } from '../queries'

interface CommunityVoicesTabProps {
  onCancel: () => void
  onSubmit: (values: CommunityVoicesFormValues) => void
  isSubmitting?: boolean
}

export const CommunityVoicesTab = ({
  onCancel,
  onSubmit,
  isSubmitting,
}: CommunityVoicesTabProps) => {
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedVoice, setSelectedVoice] = useState<CommunityVoice | null>(null)
  const [loadingVoiceId, setLoadingVoiceId] = useState<string | null>(null)
  const debouncedSearch = useDebounce(searchQuery, 500)
  const scrollContainerRef = useRef<HTMLDivElement>(null)

  const { isPlaying, play, stop } = usePlayAudio()

  const [isRetrying, setIsRetrying] = useState(false)
  const {
    data,
    isLoading,
    isFetching,
    isError,
    refetch,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useCommunityVoicesInfiniteQuery({
    search: debouncedSearch,
    enabled: debouncedSearch.trim().length >= MIN_SEARCH_LENGTH,
  })

  // Clear selected voice when search changes to avoid invalid selection
  useEffect(() => {
    setSelectedVoice(null)
  }, [debouncedSearch])

  // Flatten all pages into a single array of voices
  const voices = useMemo(() => {
    if (!data?.pages) return []
    return data.pages.flatMap((page) => page.voices)
  }, [data?.pages])

  const handlePlayPreview = async (voice: CommunityVoice) => {
    if (voice.preview_url) {
      try {
        setLoadingVoiceId(voice.voice_id)
        await play(voice.voice_id, voice.preview_url)
      } catch {
        errorToast({ title: 'Failed to play preview' })
      } finally {
        setLoadingVoiceId(null)
      }
    }
  }

  const handleSubmit = () => {
    if (selectedVoice) {
      onSubmit({
        searchQuery,
        selectedVoice,
      })
    }
  }

  // Handle scroll to load more
  const handleScroll = useCallback(() => {
    const container = scrollContainerRef.current
    if (!container) return

    const { scrollTop, scrollHeight, clientHeight } = container
    const isNearBottom = scrollHeight - scrollTop - clientHeight < 100

    if (isNearBottom && hasNextPage && !isFetchingNextPage) {
      fetchNextPage()
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  const showEmptyState = !debouncedSearch.trim()
  const showNoResults =
    debouncedSearch.trim() &&
    !isLoading &&
    !isFetching &&
    !isError &&
    !isRetrying &&
    voices.length === 0
  const showResults = voices.length > 0

  return (
    <div className="pt-4">
      <Typography variant="S/400" className="text-tertiary mb-3">
        Explore the ElevenLabs community voices
      </Typography>

      <div className="relative">
        <Input
          placeholder="Search by voice name"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          data-testid="input-community-voice-search"
        />
      </div>

      <div
        ref={scrollContainerRef}
        onScroll={handleScroll}
        className="mt-4 min-h-[240px] max-h-[300px] overflow-y-auto overflow-x-hidden"
      >
        {(isError || isRetrying) && !showEmptyState && (
          <QueryError
            title="Couldn't search community voices."
            testId="button-retry-community-voices"
            isRetrying={isRetrying}
            onRetry={async () => {
              setIsRetrying(true)
              try {
                await refetch()
              } finally {
                setIsRetrying(false)
              }
            }}
          />
        )}
        {isLoading && debouncedSearch.trim() && (
          <section className="space-y-2" aria-busy="true" aria-label="Searching community voices">
            {['first', 'second', 'third'].map((voice) => (
              <div key={voice} className="flex gap-3 rounded-md border border-st2 p-3">
                <Skeleton className="h-5 w-5 shrink-0" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-3 w-full" />
                  <Skeleton className="h-3 w-40" />
                </div>
              </div>
            ))}
          </section>
        )}

        {showEmptyState && (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <div className="rounded-full bg-muted p-4 mb-4">
              <SearchIcon className="size-8 text-tertiary" />
            </div>
            <Typography variant="S/400" className="text-tertiary max-w-[280px]">
              Search for voices from the ElevenLabs community library
            </Typography>
          </div>
        )}

        {showNoResults && (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <Typography variant="S/400" className="text-tertiary max-w-[280px]">
              No voices found for "{debouncedSearch}"
            </Typography>
          </div>
        )}

        {showResults && !isLoading && (
          <div className="flex flex-col gap-2" role="radiogroup" aria-label="Community voices">
            {voices.map((voice) => (
              <VoiceItem
                key={voice.voice_id}
                voice={voice}
                isSelected={selectedVoice?.voice_id === voice.voice_id}
                onSelect={() => setSelectedVoice(voice)}
                isPlaying={isPlaying(voice.voice_id)}
                isLoading={loadingVoiceId === voice.voice_id}
                onPlayPreview={() => handlePlayPreview(voice)}
                onStop={stop}
              />
            ))}
            {isFetchingNextPage && (
              <div className="flex items-center justify-center py-3">
                <Loader2 className="h-5 w-5 animate-spin text-tertiary" />
                <Typography variant="XS/400" className="text-tertiary ml-2">
                  Loading more...
                </Typography>
              </div>
            )}
          </div>
        )}
      </div>

      <DialogFooter className="mt-6 flex flex-row sm:justify-end gap-2">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          data-testid="button-cancel-community-voices"
        >
          Cancel
        </Button>
        <Button
          type="button"
          onClick={handleSubmit}
          disabled={!selectedVoice || isSubmitting}
          disabledReason={!selectedVoice ? 'Select a voice to add' : 'Adding voice…'}
          data-testid="button-add-community-voice"
        >
          {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
          Add Voice
        </Button>
      </DialogFooter>
    </div>
  )
}
